import { defineStore } from 'pinia';
import performanceService from '@/api/performanceService';
import feedbackRequestService from '@/api/feedbackRequestService';
import notify from '@/utils/notify';
import {decrypt} from "@/utils/crypto";

export const usePerformanceStore = defineStore('performance', {
  state: () => ({
    // Reviews list
    reviews: [],
    reviewsPagination: {
      page: 1,
      perPage: 15,
      total: 0,
      lastPage: 1
    },

    // Current review detail
    currentReview: null,
    selfReview: null,
    managerReview: null,

    // Review cycles
    reviewCycles: [],
    activeReviewCycle: null,

    // Review templates and criteria
    reviewTemplates: [],
    currentTemplate: null,
    reviewCriteria: [],
    currentCriteria: null,

    // Goals
    currentGoals: [],

    // Feedback requests
    feedbackRequests: [],
    currentFeedbackRequest: null,

    // Comments
    reviewComments: [],

    // Analytics data
    analytics: null,
    dashboardAnalytics: null,
    departmentAnalytics: null,
    employeeAnalytics: null,
    trendsData: null,
    calibrationData: null,
    reportData: null,

    // Loading states
    loading: false,
    submitting: false,
    loadingAnalytics: false
  }),

  getters: {
    // Get review by ID
    getReviewById: (state) => (id) => {
      return state.reviews.find(review => review.id === id);
    },

    // Check if user can submit self review
    canSubmitSelfReview: (state) => {
      if (!state.currentReview) return false;
      const profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));
      const employeeId = profile?.employee_id;
      return state.currentReview.status === 'self_review' &&
             state.currentReview.employee?.id === employeeId;
    },

    // Check if user can submit manager review
    canSubmitManagerReview: (state) => {
      if (!state.currentReview) return false;
      const profile = JSON.parse(decrypt(sessionStorage.getItem('profile')));

      return (state.currentReview.status === 'manager_review' ||
              state.currentReview.status === 'calibration') &&
             (state.currentReview.reviewer_id === profile.id ||
                 profile.role === 'primary');
    },

    // Get filtered reviews by status
    reviewsByStatus: (state) => (status) => {
      return state.reviews.filter(review => review.status === status);
    },

    // Get review statistics
    reviewStats: (state) => {
      const total = state.reviews.length;
      const pending = state.reviews.filter(r => r.status === 'pending').length;
      const selfReview = state.reviews.filter(r => r.status === 'self_review').length;
      const managerReview = state.reviews.filter(r => r.status === 'manager_review').length;
      const completed = state.reviews.filter(r => r.status === 'completed').length;

      return {
        total,
        pending,
        selfReview,
        managerReview,
        completed
      };
    }
  },

  actions: {
    // Fetch reviews list
    async fetchReviews(params = {}) {
      this.loading = true;
      try {
        const response = await performanceService.getReviews({
          page: params.page || this.reviewsPagination.page,
          per_page: params.perPage || this.reviewsPagination.perPage,
          status: params.status,
          cycle_id: params.cycleId,
          department_id: params.departmentId,
          search: params.search
        });

        this.reviews = response.data.data;
        this.reviewsPagination = {
          page: response.data.current_page,
          perPage: response.data.per_page,
          total: response.data.total,
          lastPage: response.data.last_page
        };

        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch reviews');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Fetch self review
    async fetchSelfReview(reviewId) {
      try {
        const response = await performanceService.getSelfReview(reviewId);
        this.selfReview = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch self review');
        throw error;
      }
    },

    // Fetch manager review
    async fetchManagerReview(reviewId) {
      try {
        const response = await performanceService.getManagerReview(reviewId);
        this.managerReview = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch manager review');
        throw error;
      }
    },


    // Create new review
    async createReview(data) {
      this.submitting = true;
      try {
        const response = await performanceService.createReview(data);
        this.reviews.unshift(response.data);
        notify.alert.success('Review created successfully');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to create review');
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    async updateReview(id, data) {
      this.submitting = true;
      try {
        const response = await performanceService.updateReview(id, data);
        this.currentReview = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to update review');
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    // Get review detail
    async fetchReview(id) {
      this.loading = true;
      try {
        const response = await performanceService.getReview(id);
        this.currentReview = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch review details');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Submit self review
    async submitSelfReview(id, data) {
      this.submitting = true;
      try {
        const response = await performanceService.submitSelfReview(id, data);
        this.currentReview = response.data;
        return response.data;
      } catch (error) {
        if (error.response?.status === 422) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            notify.alert.error(errors[key][0]);
          });
        } else {
          notify.alert.error('Failed to submit self review');
        }
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    // Submit manager review
    async submitManagerReview(id, data) {
      this.submitting = true;
      try {
        const response = await performanceService.submitManagerReview(id, data);
        this.currentReview = response.data;
        notify.alert.success('Manager review submitted successfully');
        return response.data;
      } catch (error) {
        if (error.response?.status === 422) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach(key => {
            notify.alert.error(errors[key][0]);
          });
        } else {
          notify.alert.error('Failed to submit manager review');
        }
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    // Update scores for self review responses (reviewer only)
    async updateScores(id, payload) {
      this.submitting = true;
      try {
        const response = await performanceService.updateScores(id, payload);
        this.currentReview = response.data;
        notify.alert.success('Scores updated successfully');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to update scores');
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    // Request 360 feedback
    async request360Feedback(id, users) {
      this.submitting = true;
      try {
        const response = await performanceService.request360Feedback(id, users);
        notify.alert.success('360 feedback requests sent successfully');
        await this.fetchFeedbackRequests(id);
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to send feedback requests');
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    // Add comment to review
    async addComment(id, data) {
      try {
        const response = await performanceService.addComment(id, data);
        this.reviewComments.unshift(response.data);
        notify.alert.success('Comment added successfully');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to add comment');
        throw error;
      }
    },

    // Reply to comment
    async replyToComment(reviewId, commentId, data) {
      try {
        const response = await performanceService.replyToComment(reviewId, commentId, data);
        // Find parent comment and add reply
        const parentComment = this.reviewComments.find(c => c.id === commentId);
        if (parentComment) {
          if (!parentComment.replies) parentComment.replies = [];
          parentComment.replies.push(response.data);
        }
        notify.alert.success('Reply added');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to add reply');
        throw error;
      }
    },

    // Fetch comments for review
    async fetchComments(id) {
      try {
        const response = await performanceService.getComments(id);
        this.reviewComments = response.data;
        return response.data;
      } catch (error) {
        console.error('Failed to fetch comments:', error);
        throw error;
      }
    },

    // Generate report
    async generateReport(id) {
      this.loading = true;
      try {
        const response = await performanceService.getReport(id);
        this.reportData = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to generate report');
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Fetch review cycles
    async fetchReviewCycles(id) {
      try {
        const response = await performanceService.getEmployeeReviews(id)
        this.reviewCycles = Array.isArray(response.data) ? response.data : [];
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch review cycles');
        this.reviewCycles = [];
        throw error;
      }
    },

    // Fetch active review cycle
    async fetchActiveReviewCycle() {
      try {
        const response = await performanceService.getActiveReviewCycle();
        this.activeReviewCycle = response.data;
        return response.data;
      } catch (error) {
        console.error('Failed to fetch active review cycle:', error);
        throw error;
      }
    },

    // Fetch all review templates
    async fetchReviewTemplates() {
      try {
        const response = await performanceService.getReviewTemplates();
        this.reviewTemplates = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch review templates');
        throw error;
      }
    },

    // Fetch template details with criteria
    async fetchTemplate(id) {
      try {
        const [templateRes, criteriaRes] = await Promise.all([
          performanceService.getTemplate(id),
          performanceService.getTemplateCriteria(id)
        ]);

        this.currentTemplate = templateRes.data;
        this.reviewCriteria = criteriaRes.data || [];

        return { template: this.currentTemplate, criteria: this.reviewCriteria };
      } catch (error) {
        notify.alert.error('Failed to fetch template details');
        throw error;
      }
    },

    // Fetch criteria for a template
    async fetchTemplateCriteria(templateId) {
      try {
        const response = await performanceService.getTemplateCriteria(templateId);
        this.reviewCriteria = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch review criteria');
        throw error;
      }
    },

    // Create or update criteria
    async saveCriteria(criteria) {
      try {
        const response = criteria.id
          ? await performanceService.updateCriteria(criteria.id, criteria)
          : await performanceService.createCriteria(criteria);

        // Update local state
        const index = this.reviewCriteria.findIndex(c => c.id === response.data.id);
        if (index >= 0) {
          this.reviewCriteria[index] = response.data;
        } else {
          this.reviewCriteria.push(response.data);
        }

        return response.data;
      } catch (error) {
        notify.alert.error(`Failed to ${criteria.id ? 'update' : 'create'} criteria`);
        throw error;
      }
    },

    // Delete criteria
    async deleteCriteria(criteriaId) {
      try {
        await performanceService.deleteCriteria(criteriaId);
        this.reviewCriteria = this.reviewCriteria.filter(c => c.id !== criteriaId);
      } catch (error) {
        notify.alert.error('Failed to delete criteria');
        throw error;
      }
    },

    // Fetch goals for review
    async fetchGoals(reviewId) {
      try {
        const response = await performanceService.getGoals(reviewId);
        this.currentGoals = response.data;
        return response.data;
      } catch (error) {
        console.error('Failed to fetch goals:', error);
        throw error;
      }
    },

    // Update goal
    async updateGoal(reviewId, goalId, data) {
      try {
        const response = await performanceService.updateGoal(reviewId, goalId, data);
        const index = this.currentGoals.findIndex(g => g.id === goalId);
        if (index !== -1) {
          this.currentGoals[index] = response.data;
        }
        notify.alert.success('Goal updated successfully');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to update goal');
        throw error;
      }
    },

    // Create goal
    async createGoal(reviewId, data) {
      try {
        const response = await performanceService.createGoal(reviewId, data);
        this.currentGoals.push(response.data);
        notify.alert.success('Goal created successfully');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to create goal');
        throw error;
      }
    },

    // Delete goal
    async deleteGoal(reviewId, goalId) {
      try {
        await performanceService.deleteGoal(reviewId, goalId);
        this.currentGoals = this.currentGoals.filter(g => g.id !== goalId);
        notify.alert.success('Goal deleted successfully');
      } catch (error) {
        notify.alert.error('Failed to delete goal');
        throw error;
      }
    },

    // Fetch feedback requests
    async fetchFeedbackRequests(reviewId) {
      try {
        const response = await performanceService.getFeedbackRequests(reviewId);
        this.feedbackRequests = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch feedback requests');
        throw error;
      }
    },

    // Create a single feedback request
    async createFeedbackRequest(reviewId, { requested_from, relationship }) {
      this.submitting = true;
      try {
        const requested_by = localStorage.getItem('user_id');
        const payload = {
          performance_review_id: reviewId,
          requested_from,
          requested_by,
          relationship
        };
        await feedbackRequestService.create(payload);
        notify.alert.success('Feedback request created');
        await this.fetchFeedbackRequests(reviewId);
      } catch (error) {
        notify.alert.error('Failed to create feedback request');
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    // Fetch single feedback request
    async fetchFeedbackRequest(requestId) {
      try {
        const response = await performanceService.getFeedbackRequest(requestId);
        this.currentFeedbackRequest = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch feedback request');
        throw error;
      }
    },



    // Submit 360 feedback
    async submitFeedback(data) {
      this.submitting = true;
      try {
        const response = await performanceService.submitFeedback(data);
        notify.alert.success('Feedback submitted successfully');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to submit feedback');
        throw error;
      } finally {
        this.submitting = false;
      }
    },

    // Send feedback reminder
    async sendFeedbackReminder(requestId) {
      try {
        await performanceService.sendFeedbackReminder(requestId);
        notify.alert.success('Reminder sent successfully');
      } catch (error) {
        notify.alert.error('Failed to send reminder');
        throw error;
      }
    },

    // Analytics actions
    async fetchAnalytics(params = {}) {
      this.loadingAnalytics = true;
      try {
        const response = await performanceService.getAnalytics(params);
        this.analytics = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch analytics');
        throw error;
      } finally {
        this.loadingAnalytics = false;
      }
    },

    async generateAnalyticsReport(data) {
      this.loadingAnalytics = true;
      try {
        const response = await performanceService.generateAnalyticsReport(data);
        notify.alert.success('Analytics report generated successfully');
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to generate analytics report');
        throw error;
      } finally {
        this.loadingAnalytics = false;
      }
    },

    // Export analytics as file (pdf/xlsx)
    async exportAnalytics(params = {}) {
      this.loadingAnalytics = true;
      try {
        const response = await performanceService.exportAnalytics(params);
        const blob = new Blob([response.data], {
          type: response.headers['content-type'] || 'application/octet-stream'
        });
        // Try to derive filename from Content-Disposition
        let filename = 'performance_analytics';
        const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
        if (disposition) {
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
          const encoded = match && (match[1] || match[2]);
          if (encoded) {
            try {
              filename = decodeURIComponent(encoded);
            } catch {
              filename = encoded;
            }
          }
        } else {
          const fmt = (params.format || '').toLowerCase();
          const ext = fmt === 'pdf' ? 'pdf' : (fmt === 'excel' || fmt === 'xlsx') ? 'xlsx' : 'dat';
          const stamp = new Date().toISOString().slice(0,19).replace(/[:T]/g,'-');
          filename = `performance-analytics-${stamp}.${ext}`;
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        notify.alert.error('Failed to export analytics');
        throw error;
      } finally {
        this.loadingAnalytics = false;
      }
    },

    async fetchCalibrationData() {
      this.loadingAnalytics = true;
      try {
        const response = await performanceService.getCalibrationData();
        this.calibrationData = response.data;
        return response.data;
      } catch (error) {
        notify.alert.error('Failed to fetch calibration data');
        throw error;
      } finally {
        this.loadingAnalytics = false;
      }
    },

    // Reset store
    resetStore() {
      this.reviews = [];
      this.currentReview = null;
      this.reviewComments = [];
      this.currentGoals = [];
      this.feedbackRequests = [];
      this.reportData = null;
    }
  }
});
